import {
  ApiError,
  GamePass,
  RemoteNotificationTrigger,
} from "@blacknut/javascript-sdk/dist";
import styles from "./styles.module.scss";
import { getGamePass } from "@blacknut/javascript-sdk/dist/api/BlacknutPlus/getGamePass";
import { logD, logE } from "@blacknut/logging/dist";
import { ModalSubscription, useModal } from "@blacknut/react-sdk";
import {
  AppLayout,
  NotificationMessage,
  NotificationMessageKind,
  notificationService,
  State,
  useAnalytics,
  useLayout,
} from "core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { firstValueFrom } from "rxjs";
import useSubscribed from "src/hooks/useSubscribed";
import { LOGGING_TAG } from "../Utils";
import { useErrorHandling } from "../V2ErrorHandler";
import React from "react";
import { useAppNavigation } from "../Navigation/useAppNavigation";
import { useLocation } from "react-router-dom";
import { useTheme } from "src/theme/ThemeProvider";
import { QRCodeModal } from "src/components/Modals/QRCodeModal";
import { isTryBuy } from "../subscription";
import SwitchToMasterModal from "../SwitchToMasterModal";
import { HeaderElementModal } from "src/components/Modals/HeaderElementModal";
import { useSignupURL } from "../useSignupURL";
import { useWarioURL } from "../useWarioURL";
import { useUbisoftPremium } from "./useUbisoftPremium";
import electronService from "src/services/ElectronService";
export const _redirectToOffer = (
  modalSubscription: React.MutableRefObject<ModalSubscription | undefined>,
  offerURL: URL,
) => {
  modalSubscription.current?.remove();
  modalSubscription.current = undefined;

  //Le's defer redirect, otherwise, redirect will go the / but modal close will go back
  //Stream will be relaunched
  setTimeout(() => {
    if (electronService.isAvailable()) {
      window.open(offerURL, "_blank");
    } else {
      window.location.replace(offerURL);
    }
  }, 500);
};

interface PassTVModalProps {
  gamePass: GamePass;
  url: string;
  onClose: () => void;
  isOpen: boolean;
  title?: string;
  description?: string;
  onSubscribe?: () => void;
}

export const PassModal = (props: PassTVModalProps) => {
  const { gamePass, url, onClose, title, description, onSubscribe } = props;
  const { t } = useTranslation();
  const { theme } = useTheme();
  const layout = useLayout();
  const background = gamePass.images.find((im) => im.kind === "background");
  const logo = gamePass.images.find((im) => im.kind === "logo");
  if (layout === AppLayout.TV) {
    return (
      <QRCodeModal
        {...props}
        title={title ? title : gamePass.pre_subscription.title}
        description={description ? description : gamePass.pre_subscription.description}
        btnText={t("dialogs.unlockPass.no")}
        URL={url}
        onClose={onClose}
        theme={theme}
        headerElement={
          <div className={styles.background}>
            <img className={styles.logo} src={logo?.url} />
            <img className={styles.background} src={background?.url} />
          </div>
        }
      />
    );
  }
  return (
    <HeaderElementModal
      {...props}
      title={title ? title : gamePass.pre_subscription.title}
      description={description ? description : gamePass.pre_subscription.description}
      onClose={onClose}
      positiveButtonText={t("buttons.confirm")}
      negativeButtonText={t("buttons.cancel")}
      onPressPositive={onSubscribe}
      onPressNegative={onClose}
      headerElement={
        <div className={styles.background}>
          <img className={styles.logo} src={logo?.url} />
          <img className={styles.background} src={background?.url} />
        </div>
      }
    />
  );
};

export const useGamePassUnlock = ({ passId }: { passId?: string }) => {
  const { profile } = useSelector((state: State) => state.profilesState);
  const { t } = useTranslation();
  const layout = useLayout();
  const { push: modalPush } = useModal();
  const modalSubscription = useRef<ModalSubscription>();
  const location = useLocation();
  const pathname = location.pathname;
  const { createSubURL } = useSignupURL({
    ...(passId && { uuid: passId, bundle: "false" }),
  });
  const { create } = useWarioURL();

  const { trackPass } = useAnalytics();
  const dispatch = useDispatch();

  const { user } = useSelector((state: State) => state.globalState);
  const { subscription, userToken } = useSelector((state: State) => state.globalState);
  const _isTryBuy = isTryBuy(subscription.mainEntitlement);

  const { navigate } = useAppNavigation();
  const [error, setError] = useState<ApiError>();
  useErrorHandling({
    error,
    clearError: () => {
      setError(undefined);
    },
  });
  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);

  const { checkForValidSubscription } = useSubscribed(passId);
  const { checkForUbisoftPremiumUpgrade } = useUbisoftPremium();

  useEffect(() => {
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload as any;

      if (notification.trigger === RemoteNotificationTrigger.PLAN_SUBSCRIBED) {
        closeModal();
      }
      if (notification.trigger === RemoteNotificationTrigger.UBISOFT_CONNECT) {
        if (notification.status === "success") {
          closeModal();
        }
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [closeModal, dispatch, navigate, user]);

  const unlockPass = useCallback(
    async (gameId?: string, isUbi?: boolean) => {
      passId && trackPass(passId, gameId);

      if (!profile?.isMaster) {
        logD(LOGGING_TAG, "Profile is not master");

        const redirectUrl = pathname;
        modalSubscription.current = modalPush((props) => (
          <SwitchToMasterModal
            {...props}
            redirectUrl={redirectUrl}
            closeModal={closeModal}
          />
        ));
        return;
      }

      // FIXME no eror handling here
      const gamePass = passId ? await firstValueFrom(getGamePass(passId)) : undefined;

      // FIXME no eror handling here
      if (!(await checkForValidSubscription(gameId || "", gamePass && gamePass))) {
        return;
      }

      if (_isTryBuy || !gamePass) return;

      logD(LOGGING_TAG, "Profile is master");
      try {
        let warioUrl = undefined;

        if (isUbi) {
          // Will return to Wario, for SSO
          warioUrl = await create(
            `/dashboard/subscriptions/pass/${gamePass.offer.uuid}/checkout?token=${userToken?.accessToken}`,
            undefined,
            { tokens: true },
          );
        }

        const subURL = await createSubURL(warioUrl);

        if (!(await checkForUbisoftPremiumUpgrade(isUbi, gamePass))) {
          return;
        }

        if (layout === AppLayout.TV) {
          modalSubscription.current = modalPush((props) => (
            <PassModal
              {...props}
              onClose={closeModal}
              url={subURL.toString()}
              gamePass={gamePass}
              title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
              description={t("dialogs.pass.tvSubscribe.message")}
            />
          ));
        } else {
          modalSubscription.current = modalPush((props) => (
            <PassModal
              {...props}
              onClose={closeModal}
              gamePass={gamePass}
              url={subURL.toString()}
              onSubscribe={() => _redirectToOffer(modalSubscription, subURL)}
              title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
              description={t("dialogs.pass.subscribe.message")}
            />
          ));
        }
      } catch (e) {
        logE(LOGGING_TAG, "Error BN+ get pass", e);
        setError(e as ApiError);
      }
    },
    [
      _isTryBuy,
      checkForUbisoftPremiumUpgrade,
      checkForValidSubscription,
      closeModal,
      create,
      createSubURL,
      layout,
      modalPush,
      passId,
      pathname,
      profile?.isMaster,
      t,
      trackPass,
      userToken?.accessToken,
    ],
  );
  return { unlockPass };
};

export default useGamePassUnlock;
